/**
 * Time utilities using Luxon for proper timezone handling
 *
 * All datetimes are:
 * - Stored/transmitted as UTC (ISO 8601 with Z suffix)
 * - Displayed in America/New_York timezone
 */
import { DateTime } from 'luxon';

export const DISPLAY_TIMEZONE = 'America/New_York';

/**
 * Parse an ISO datetime string from the API (UTC) into a Luxon DateTime in display timezone
 */
export const parseApiDateTime = (isoString) => {
  if (!isoString) return null;
  // API sends UTC (with Z) - parse and convert to display timezone
  return DateTime.fromISO(isoString, { zone: 'utc' }).setZone(DISPLAY_TIMEZONE);
};

/**
 * Normalize any datetime input to a Luxon DateTime in display timezone
 * Accepts: Luxon DateTime, JS Date, or ISO string
 */
export const toLuxon = (dt) => {
  if (!dt) return null;
  if (DateTime.isDateTime(dt)) return dt;
  if (dt instanceof Date) return DateTime.fromJSDate(dt, { zone: DISPLAY_TIMEZONE });
  return parseApiDateTime(dt);
};

/**
 * Convert a Luxon DateTime or JS Date to UTC ISO string for API
 */
export const toApiDateTime = (dt) => {
  if (!dt) return null;
  const luxonDt = DateTime.isDateTime(dt)
    ? dt
    : DateTime.fromJSDate(dt, { zone: DISPLAY_TIMEZONE });
  return luxonDt.toUTC().toISO();
};

/**
 * Create a Luxon DateTime from JS Date, assuming it's in display timezone
 */
export const fromJSDate = (date) => {
  if (!date) return null;
  return DateTime.fromJSDate(date, { zone: DISPLAY_TIMEZONE });
};

/**
 * Convert Luxon DateTime to JS Date (for components that need it)
 */
export const toJSDate = (dt) => {
  if (!dt) return null;
  const luxonDt = toLuxon(dt);
  return luxonDt?.toJSDate() ?? null;
};

/**
 * Format time for display (e.g., "9:00 AM")
 * Accepts: Luxon DateTime, JS Date, or ISO string
 */
export const formatTime = (dt) => {
  if (!dt) return '';
  const luxonDt = toLuxon(dt);
  return luxonDt?.toFormat('h:mm a') ?? '';
};

/**
 * Format time compactly for calendar cells
 * On the :00 → "8AM" (no space, no minutes)
 * Has minutes → "8:45" (no AM/PM suffix)
 */
export const formatTimeCompact = (dt) => {
  if (!dt) return '';
  const luxonDt = toLuxon(dt);
  if (!luxonDt) return '';

  if (luxonDt.minute === 0) {
    return luxonDt.toFormat('ha'); // "8AM"
  } else {
    return luxonDt.toFormat('h:mm'); // "8:45"
  }
};

/**
 * Format date for display (e.g., "Monday, April 15")
 */
export const formatDate = (dt) => {
  if (!dt) return '';
  const luxonDt = toLuxon(dt);
  return luxonDt?.toFormat('cccc, LLLL d') ?? '';
};

/**
 * Format full date for display (e.g., "Monday, April 15, 2025")
 */
export const formatDateFull = (dt) => {
  if (!dt) return '';
  const luxonDt = toLuxon(dt);
  return luxonDt?.toFormat('cccc, LLLL d, yyyy') ?? '';
};

/**
 * Format shift time range (e.g., "9:00 AM - 5:00 PM")
 */
export const formatShiftTime = (start, end) => {
  return `${formatTime(start)} - ${formatTime(end)}`;
};

/**
 * Get date string in YYYY-MM-DD format (in display timezone)
 */
export const getDateString = (dt) => {
  if (!dt) return '';
  const luxonDt = toLuxon(dt);
  return luxonDt?.toFormat('yyyy-MM-dd') ?? '';
};

/**
 * Check if two datetimes are on the same day (in display timezone)
 */
export const isSameDay = (dt1, dt2) => {
  if (!dt1 || !dt2) return false;
  const ldt1 = toLuxon(dt1);
  const ldt2 = toLuxon(dt2);
  return ldt1?.hasSame(ldt2, 'day') ?? false;
};

/**
 * Create a DateTime for today at a specific time in display timezone
 */
export const todayAt = (hour, minute = 0) => {
  return DateTime.now().setZone(DISPLAY_TIMEZONE).set({ hour, minute, second: 0, millisecond: 0 });
};

/**
 * Create a DateTime for a specific date at a specific time in display timezone
 */
export const dateAt = (dateString, hour, minute = 0) => {
  return DateTime.fromISO(dateString, { zone: DISPLAY_TIMEZONE }).set({ hour, minute, second: 0, millisecond: 0 });
};

/**
 * Calculate hours between two datetimes
 */
export const calculateHours = (start, end) => {
  if (!start || !end) return 0;
  const startDt = toLuxon(start);
  const endDt = toLuxon(end);
  if (!startDt || !endDt) return 0;
  const diff = endDt.diff(startDt, 'hours');
  return Math.round(diff.hours * 100) / 100;
};

/**
 * Parse a time string like "9:30 AM" or "14:00" and apply to a reference date
 */
export const parseTimeString = (timeStr, referenceDate) => {
  if (!timeStr || !referenceDate) return null;

  const refDt = toLuxon(referenceDate);

  // Try various formats
  const formats = ['h:mm a', 'h:mma', 'H:mm', 'h a', 'ha'];
  const normalized = timeStr.trim().toUpperCase().replace(/\s+/g, ' ');

  for (const fmt of formats) {
    const parsed = DateTime.fromFormat(normalized, fmt, { zone: DISPLAY_TIMEZONE });
    if (parsed.isValid) {
      return refDt.set({ hour: parsed.hour, minute: parsed.minute, second: 0, millisecond: 0 });
    }
  }

  return null;
};

// Re-export DateTime for components that need direct access
export { DateTime };
